import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { Dialog } from "../../../../../../../components/Dialogs/Dialog";
import {
  CustomFormLabel,
  CustomTextField,
} from "../../../../../../../components/formsComponents";
import { useDispatchWrapper } from "../../../../../../../hooks";
import { decodeVinAsync } from "../../../../../../../redux/vinDecoder/vinDecoderActions";
import { IVinDecodeResponse } from "../../../../../../../redux/vinDecoder/vinDecoder.types";
import { isAlphaNumeric } from "../../../../../../../helpers";
import { showMessage } from "../../../../../../../redux/messages/messagesActions";
import { mapVinResponseToFields } from "./AutomobileVehicleInformation";

export interface IVerifyVinApplicationData {
  vin?: string | null;
  year?: string | null;
  make?: string | null;
  model?: string | null;
  body_type?: string | null;
  // Optional: some application forms do not capture a vehicle type. When the
  // key is omitted the "Vehicle Type" comparison row is hidden.
  vehicle_type?: string | null;
}

interface IVerifyVinDialogProps {
  open: boolean;
  vin: string | null;
  applicationData: IVerifyVinApplicationData;
  onClose: () => void;
  // Called when the user chooses "Verify and Update".
  // Receives the mapped decoded fields to apply to the form.
  onUpdate: (fields: Record<string, string | undefined>) => void;
}

interface IComparisonRow {
  label: string;
  decoded?: string;
  application?: string;
  matched: boolean;
}

const normalize = (value?: string | null): string =>
  (value ?? "").toString().trim().toUpperCase();

export const VerifyVinDialog: React.FC<IVerifyVinDialogProps> = (props) => {
  const { open, vin, applicationData, onClose, onUpdate } = props;

  const dispatch = useDispatchWrapper();
  const [vinInput, setVinInput] = React.useState<string>(vin || "");
  const [loading, setLoading] = React.useState(false);
  const [decodedResponse, setDecodedResponse] =
    React.useState<IVinDecodeResponse | null>(null);

  // Keep the decode field in sync with the passed VIN whenever the dialog opens.
  React.useEffect(() => {
    if (open) {
      setVinInput(vin || "");
      setDecodedResponse(null);
    }
  }, [open, vin]);

  const handleDecode = () => {
    const trimmed = (vinInput || "").trim().toUpperCase();
    if (!trimmed || trimmed.length !== 17 || !isAlphaNumeric(trimmed)) {
      dispatch(
        showMessage({
          type: "error",
          message: "Please enter a valid 17 digit alphanumeric VIN.",
          displayAs: "snackbar",
        })
      );
      return;
    }
    setLoading(true);
    dispatch(
      decodeVinAsync(trimmed, (isSuccess, data) => {
        setLoading(false);
        if (isSuccess && data) {
          setDecodedResponse(data);
        }
      })
    );
  };

  const decodedFields = React.useMemo(
    () => (decodedResponse ? mapVinResponseToFields(decodedResponse) : null),
    [decodedResponse]
  );

  const rows: IComparisonRow[] = React.useMemo(() => {
    if (!decodedFields) return [];
    const definitions: {
      label: string;
      decoded?: string;
      application?: string | null;
    }[] = [
      {
        label: "VIN",
        decoded: decodedResponse?.vin,
        application: applicationData.vin,
      },
      {
        label: "Year",
        decoded: decodedFields.year,
        application: applicationData.year,
      },
      {
        label: "Make",
        decoded: decodedFields.make,
        application: applicationData.make,
      },
      {
        label: "Model",
        decoded: decodedFields.model,
        application: applicationData.model,
      },
      {
        label: "Body Type",
        decoded: decodedFields.body_type,
        application: applicationData.body_type,
      },
    ];

    // Only show the Vehicle Type row when the form captures a vehicle type.
    if ("vehicle_type" in applicationData) {
      definitions.push({
        label: "Vehicle Type",
        decoded: decodedFields.vehicle_type,
        application: applicationData.vehicle_type,
      });
    }

    return definitions.map((def) => ({
      label: def.label,
      decoded: def.decoded,
      application: def.application ?? undefined,
      matched: normalize(def.decoded) === normalize(def.application),
    }));
  }, [decodedFields, decodedResponse, applicationData]);

  const matchedCount = rows.filter((r) => r.matched).length;
  const allMatched = rows.length > 0 && matchedCount === rows.length;

  const handleVerifyAndUpdate = () => {
    if (decodedFields) {
      onUpdate(decodedFields);
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      title="Verify VIN"
      size="md"
      fullWidth
      onClose={onClose}
      actions={[
        {
          type: "button",
          variant: "outlined",
          label: "Verify",
          disabled: !decodedResponse,
          onClick: onClose,
        },
        {
          type: "button",
          variant: "contained",
          label: "Verify and Update",
          disabled: !decodedResponse,
          onClick: handleVerifyAndUpdate,
        },
      ]}
    >
      {/* VIN decode input */}
      <Paper
        variant="outlined"
        sx={{ p: 2, borderRadius: 2, backgroundColor: "grey.50" }}
      >
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} sm={8}>
            <CustomFormLabel sx={{ mt: 0 }}>VIN Number</CustomFormLabel>
            <CustomTextField
              name="verify_vin"
              variant="outlined"
              size="small"
              type="text"
              fullWidth
              value={vinInput}
              inputProps={{ maxLength: 17, style: { letterSpacing: 1 } }}
              onChange={(e) => setVinInput(e.target.value.toUpperCase())}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={handleDecode}
              disabled={loading}
              startIcon={
                loading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : undefined
              }
            >
              {loading ? "Decoding..." : "Decode"}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Comparison result */}
      {decodedResponse && (
        <Box mt={3}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            mb={1.5}
          >
            <Typography variant="h5" fontWeight={600}>
              Comparison Result
            </Typography>
            <Chip
              size="small"
              icon={
                allMatched ? (
                  <CheckCircleIcon fontSize="small" />
                ) : (
                  <CancelIcon fontSize="small" />
                )
              }
              color={allMatched ? "success" : "warning"}
              label={
                allMatched
                  ? "All fields match"
                  : `${matchedCount}/${rows.length} fields match`
              }
              sx={{ fontWeight: 600 }}
            />
          </Stack>

          {!allMatched && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Some fields differ from the application. Use "Verify and Update"
              to replace them with the decoded values.
            </Alert>
          )}

          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "grey.100" }}>
                  <TableCell sx={{ fontWeight: 700 }}>Field</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Decoded Data</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    Application Data
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">
                    Match
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow
                    key={row.label}
                    sx={{
                      "&:nth-of-type(odd)": { backgroundColor: "grey.50" },
                      "&:last-child td, &:last-child th": { border: 0 },
                    }}
                  >
                    <TableCell sx={{ fontWeight: 600 }}>{row.label}</TableCell>
                    <TableCell>{row.decoded || "-"}</TableCell>
                    <TableCell
                      sx={{ color: row.matched ? "text.primary" : "error.main" }}
                    >
                      {row.application || "-"}
                    </TableCell>
                    <TableCell align="center">
                      {row.matched ? (
                        <CheckCircleIcon sx={{ color: "success.main" }} />
                      ) : (
                        <CancelIcon sx={{ color: "error.main" }} />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Dialog>
  );
};
